import React, { useCallback, useEffect, useState } from "react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../common/SafeIcon";
import { useClient } from "../../context/ClientContext";
import {
  getClientSettings,
  updateClientBusinessProfile,
  updateOwnPassword,
  updateOwnProfile,
} from "../../services/client/clientSettingsService";
import BusinessProfileSettings from "../../components/clients/settings/BusinessProfileSettings";
import MyProfileSettings from "../../components/clients/settings/MyProfileSettings";
import SecuritySettings from "../../components/clients/settings/SecuritySettings";
import PreferencesSettings from "../../components/clients/settings/PreferencesSettings";
import { SettingsNotice } from "../../components/clients/settings/SettingsPanel";

const { FiBriefcase, FiUser, FiShield, FiSliders, FiAlertCircle } = FiIcons;

const sections = [
  { id: "business", label: "Business Profile", icon: FiBriefcase },
  { id: "profile", label: "My Profile", icon: FiUser },
  { id: "security", label: "Security", icon: FiShield },
  { id: "preferences", label: "Preferences", icon: FiSliders },
];

export default function ClientSettings() {
  const {
    user,
    activeWorkspace,
    workspaceType,
  } = useClient();
  const organizationId =
    workspaceType === "client" ? activeWorkspace?.id : null;

  const [activeSection, setActiveSection] = useState("business");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!organizationId || !user?.id) {
      setSettings(null);
      setError("Select an active client organization to view its settings.");
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError("");

    try {
      const result = await getClientSettings({
        organizationId,
        userId: user.id,
      });
      setSettings(result);
      return result;
    } catch (loadError) {
      setSettings(null);
      setError(loadError.message || "Unable to load Settings.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [organizationId, user?.id]);

  useEffect(() => {
    let current = true;
    setSettings(null);
    setLoading(true);
    setError("");

    if (!organizationId || !user?.id) {
      setError("Select an active client organization to view its settings.");
      setLoading(false);
      return undefined;
    }

    getClientSettings({
      organizationId,
      userId: user.id,
    })
      .then((result) => {
        if (current) setSettings(result);
      })
      .catch((loadError) => {
        if (current) {
          setError(loadError.message || "Unable to load Settings.");
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [organizationId, user?.id]);

  const saveBusiness = async (changes) => {
    const updated = await updateClientBusinessProfile({
      organizationId,
      userId: user.id,
      ...changes,
    });
    setSettings(updated);
    return updated;
  };

  const saveProfile = async (changes) => {
    const profile = await updateOwnProfile({
      userId: user.id,
      changes,
    });
    setSettings((current) => current
      ? { ...current, profile }
      : current);
    return profile;
  };

  const savePassword = (password) =>
    updateOwnPassword({
      userId: user.id,
      password,
    });

  const visibleSettings =
    settings?.organizationId === organizationId
      ? settings
      : null;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header>
        <p className="text-sm font-semibold text-emerald-700">
          {activeWorkspace?.name || "Client Portal"}
        </p>
        <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-900">
          Settings
        </h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Manage your business information, personal account, and preferences.
        </p>
      </header>

      <nav
        aria-label="Settings sections"
        className="flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm"
      >
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            aria-current={activeSection === section.id ? "page" : undefined}
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
              activeSection === section.id
                ? "bg-emerald-50 text-emerald-800"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <SafeIcon icon={section.icon} />
            {section.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm" aria-live="polite">
          <span className="mx-auto block h-8 w-8 animate-spin rounded-full border-2 border-emerald-100 border-t-emerald-700" />
          <p className="mt-4 text-sm text-slate-600">Loading your settings…</p>
        </div>
      ) : error ? (
        <SettingsNotice kind="error">
          <span className="inline-flex items-center gap-2">
            <FiAlertCircle />
            {error}
          </span>
        </SettingsNotice>
      ) : visibleSettings ? (
        <>
          {activeSection === "business" && (
            <BusinessProfileSettings
              settings={visibleSettings}
              onSaved={saveBusiness}
            />
          )}
          {activeSection === "profile" && (
            <MyProfileSettings
              profile={visibleSettings.profile}
              email={visibleSettings.auth.email}
              role={visibleSettings.membershipRole}
              userId={user.id}
              onSaved={saveProfile}
            />
          )}
          {activeSection === "security" && (
            <SecuritySettings
              auth={visibleSettings.auth}
              userId={user.id}
              onPasswordChanged={savePassword}
            />
          )}
          {activeSection === "preferences" && (
            <PreferencesSettings />
          )}
        </>
      ) : null}
    </div>
  );
}